import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, ArrowLeft, Upload, Download, Trash2, Edit,
  Calculator, Check, AlertCircle, Clock, Send, CheckCircle, XCircle,
  ChevronRight, Building2, User, Calendar, Hash, FileSignature,
  Loader2, Eye, RefreshCw, Scale, Briefcase, ArrowRight, X, Plus,
  Save, FileCheck, FileWarning, PenTool, Mail, FileInput, Gavel,
  AlertTriangle, CheckCheck, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { extrajudicialAPI, contactosAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Configuración
const ESTADOS_CONFIG = {
  borrador: { nombre: 'Borrador', color: 'bg-gray-100 text-gray-700', icon: Edit },
  pendiente_firma: { nombre: 'Pendiente Firma', color: 'bg-yellow-100 text-yellow-700', icon: FileSignature },
  enviado_banco: { nombre: 'Enviado al Banco', color: 'bg-blue-100 text-blue-700', icon: Send },
  pendiente_respuesta: { nombre: 'Pendiente Respuesta', color: 'bg-orange-100 text-orange-700', icon: Clock },
  finalizado: { nombre: 'Finalizado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  convertido_expediente: { nombre: 'Convertido a Expediente', color: 'bg-purple-100 text-purple-700', icon: Briefcase },
};

const TIPOS_CONCEPTO = [
  { id: 'gastos_notaria', nombre: 'Gastos de Notaría', porcentaje: 50, grupo: 'gastos' },
  { id: 'gastos_registro', nombre: 'Gastos de Registro', porcentaje: 100, grupo: 'gastos' },
  { id: 'gastos_gestoria', nombre: 'Gastos de Gestoría', porcentaje: 100, grupo: 'gastos' },
  { id: 'gastos_tasacion', nombre: 'Gastos de Tasación', porcentaje: 100, grupo: 'gastos' },
  { id: 'comision_apertura', nombre: 'Comisión de Apertura', porcentaje: 100, grupo: 'apertura' },
  { id: 'posicion_deudora', nombre: 'Posición Deudora', porcentaje: 100, grupo: 'posiciones' },
];

const CLAUSULAS_NULIDAD = [
  { id: 'clausula_suelo', nombre: 'Cláusula Suelo', descripcion: 'Limitación a la variabilidad del tipo de interés' },
  { id: 'clausula_redondeo', nombre: 'Redondeo al Alza', descripcion: 'Redondeo del tipo de interés al alza' },
  { id: 'clausula_interes_demora', nombre: 'Interés de Demora', descripcion: 'Intereses de demora abusivos' },
  { id: 'clausula_cierre_irph', nombre: 'Cierre IRPH', descripcion: 'Índice de referencia IRPH' },
];

const RESULTADOS_CONFIG = {
  pendiente: { nombre: 'Pendiente', color: 'bg-gray-100 text-gray-700' },
  estimada: { nombre: 'Estimada', color: 'bg-green-100 text-green-700' },
  desestimada: { nombre: 'Desestimada', color: 'bg-red-100 text-red-700' },
  parcial: { nombre: 'Parcialmente Estimada', color: 'bg-yellow-100 text-yellow-700' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES');
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0,00 €';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
};

// =============================================================================
// COMPONENTE PRINCIPAL: Detalle de Reclamación
// =============================================================================
const ReclamacionDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reclamacion, setReclamacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('datos');

  // Estados del formulario
  const [datosHipoteca, setDatosHipoteca] = useState({});
  const [clausulas, setClausulas] = useState({});
  
  // Selector de entidad bancaria - igual que búsqueda de clientes
  const [bancosEncontrados, setBancosEncontrados] = useState([]);
  const [searchBanco, setSearchBanco] = useState('');
  const [loadingBancos, setLoadingBancos] = useState(false);
  const [showBancoDropdown, setShowBancoDropdown] = useState(false);
  const [isCreatingBanco, setIsCreatingBanco] = useState(false);
  const [nuevoBanco, setNuevoBanco] = useState({ 
    nombre: '', razon_social: '', cif: '', direccion: '', codigo_postal: '', ciudad: '', provincia: '', notas: '' 
  });

  // Dialogs
  const [isConceptoDialogOpen, setIsConceptoDialogOpen] = useState(false);
  const [isEstadoDialogOpen, setIsEstadoDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: '', nombre: '' });
  
  // Dialogs de seguimiento
  const [isFirmaDialogOpen, setIsFirmaDialogOpen] = useState(false);
  const [isEnvioDialogOpen, setIsEnvioDialogOpen] = useState(false);
  const [isRespuestaDialogOpen, setIsRespuestaDialogOpen] = useState(false);
  const [isConvertirDialogOpen, setIsConvertirDialogOpen] = useState(false);
  const [isEditClienteDialogOpen, setIsEditClienteDialogOpen] = useState(false);

  const loadReclamacion = useCallback(async () => {
    try {
      setLoading(true);
      const res = await extrajudicialAPI.getById(id);
      setReclamacion(res.data);
      
      // Cargar datos en formularios
      setDatosHipoteca({
        fecha_firma_hipoteca: res.data.fecha_firma_hipoteca || '',
        entidad_bancaria_actual: res.data.entidad_bancaria_actual || '',
        entidad_bancaria_id: res.data.entidad_bancaria_id || null,
        entidad_bancaria_anterior: res.data.entidad_bancaria_anterior || '',
        hipoteca_vigente: res.data.hipoteca_vigente ?? true,
        fecha_cancelacion_hipoteca: res.data.fecha_cancelacion_hipoteca || '',
        nombre_notario: res.data.nombre_notario || '',
        numero_protocolo: res.data.numero_protocolo || '',
        localidad_notario: res.data.localidad_notario || ''
      });
      
      setClausulas({
        clausula_gastos: res.data.clausula_gastos || false,
        clausula_comision_apertura: res.data.clausula_comision_apertura || false,
        clausula_posiciones_deudoras: res.data.clausula_posiciones_deudoras || false,
        posiciones_deudoras_con_soportes: res.data.posiciones_deudoras_con_soportes || false,
        clausula_suelo: res.data.clausula_suelo || false,
        clausula_redondeo: res.data.clausula_redondeo || false,
        clausula_interes_demora: res.data.clausula_interes_demora || false,
        clausula_cierre_irph: res.data.clausula_cierre_irph || false,
        clausula_personalizada: res.data.clausula_personalizada || '',
        clausula_personalizada_nombre: res.data.clausula_personalizada_nombre || ''
      });
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error', description: 'No se pudo cargar la reclamación', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadReclamacion();
  }, [loadReclamacion]);

  // Cargar lista de bancos/entidades (tipo empresa o contraparte)
  // Búsqueda activa como en clientes
  const handleSearchBancos = async (termino) => {
    if (!termino || termino.length < 2) {
      setBancosEncontrados([]);
      return;
    }
    setLoadingBancos(true);
    try {
      // Buscar en todos los contactos, sin filtrar por tipo
      const res = await contactosAPI.getAll({ search: termino, limit: 15 });
      const resultados = res.data?.data || res.data?.items || res.data || [];
      setBancosEncontrados(Array.isArray(resultados) ? resultados : []);
    } catch (error) {
      console.error('Error buscando bancos:', error);
      setBancosEncontrados([]);
    } finally {
      setLoadingBancos(false);
    }
  };

  // Crear nuevo banco/entidad - formulario completo como contactos
  const handleCrearBanco = async () => {
    if (!nuevoBanco.nombre) {
      toast({ title: 'Error', description: 'El nombre es obligatorio', variant: 'destructive' });
      return;
    }
    try {
      setSaving(true);
      const res = await contactosAPI.create({
        nombre: nuevoBanco.nombre,
        razon_social: nuevoBanco.razon_social || nuevoBanco.nombre,
        tipo_entidad: 'empresa',
        tipo: 'parte_contraria',  // Bancos son parte contraria
        nif_cif: nuevoBanco.cif || '',
        direccion: nuevoBanco.direccion || '',
        codigo_postal: nuevoBanco.codigo_postal || '',
        ciudad: nuevoBanco.ciudad || '',
        provincia: nuevoBanco.provincia || '',
        notas: nuevoBanco.notas || ''
      });
      const newBanco = res.data;
      setDatosHipoteca(prev => ({
        ...prev,
        entidad_bancaria_actual: newBanco.nombre || newBanco.razon_social,
        entidad_bancaria_id: newBanco.id
      }));
      setIsCreatingBanco(false);
      setNuevoBanco({ nombre: '', razon_social: '', cif: '', direccion: '', codigo_postal: '', ciudad: '', provincia: '', notas: '' });
      setSearchBanco('');
      setShowBancoDropdown(false);
      toast({ title: 'Entidad creada', description: `${newBanco.nombre} añadido como contraparte` });
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear la entidad bancaria', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Seleccionar banco existente
  const handleSelectBanco = (banco) => {
    setDatosHipoteca(prev => ({
      ...prev,
      entidad_bancaria_actual: banco.nombre || banco.razon_social,
      entidad_bancaria_id: banco.id
    }));
    setSearchBanco('');
    setBancosEncontrados([]);
    setShowBancoDropdown(false);
  };

  const handleSaveDatosHipoteca = async () => {
    setSaving(true);
    try {
      // Limpiar datos antes de enviar - convertir strings vacías a null
      const cleanedData = {
        ...datosHipoteca,
        fecha_firma_hipoteca: datosHipoteca.fecha_firma_hipoteca || null,
        fecha_cancelacion_hipoteca: datosHipoteca.fecha_cancelacion_hipoteca || null,
        entidad_bancaria_actual: datosHipoteca.entidad_bancaria_actual || null,
        entidad_bancaria_id: datosHipoteca.entidad_bancaria_id || null,
        entidad_bancaria_anterior: datosHipoteca.entidad_bancaria_anterior || null,
        nombre_notario: datosHipoteca.nombre_notario || null,
        numero_protocolo: datosHipoteca.numero_protocolo || null,
        localidad_notario: datosHipoteca.localidad_notario || null,
      };
      await extrajudicialAPI.updateDatosHipoteca(id, cleanedData);
      toast({ title: 'Datos guardados' });
      loadReclamacion();
    } catch (error) {
      console.error('Error guardando datos hipoteca:', error);
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudieron guardar los datos', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClausulas = async () => {
    setSaving(true);
    try {
      await extrajudicialAPI.updateClausulas(id, clausulas);
      toast({ title: 'Cláusulas guardadas' });
      loadReclamacion();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerarDocumento = async (tipo) => {
    try {
      toast({ title: 'Generando documento...' });
      const res = await extrajudicialAPI.generarDocumento(id, tipo);
      toast({ title: 'Documento generado', description: res.data.nombre_archivo });
      loadReclamacion();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo generar el documento', variant: 'destructive' });
    }
  };

  const handleGenerarResumenPDF = async () => {
    try {
      toast({ title: 'Generando resumen PDF...' });
      const res = await extrajudicialAPI.generarResumenPDF(id);
      toast({ title: 'Resumen generado', description: res.data.nombre_archivo });
      loadReclamacion();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo generar el resumen', variant: 'destructive' });
    }
  };

  const handleDownloadDocumento = async (docId, nombre) => {
    try {
      const res = await extrajudicialAPI.downloadDocumento(id, docId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = nombre;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo descargar', variant: 'destructive' });
    }
  };

  const handleDeleteDocumento = async (docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await extrajudicialAPI.deleteDocumento(id, docId);
      toast({ title: 'Documento eliminado' });
      loadReclamacion();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handlePreviewDocumento = (docId, nombre) => {
    const previewUrl = extrajudicialAPI.previewDocumento(id, docId);
    setPreviewDialog({ open: true, url: previewUrl, nombre: nombre });
  };

  const handleConvertirExpediente = async () => {
    if (!window.confirm('¿Convertir esta reclamación en un expediente judicial?')) return;
    try {
      const res = await extrajudicialAPI.convertirAExpediente(id);
      toast({ title: 'Expediente creado', description: res.data.numero_expediente });
      navigate(`/expedientes/${res.data.expediente_id}`);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudo convertir', variant: 'destructive' });
    }
  };

  const handleDeleteReclamacion = async () => {
    if (!window.confirm(`¿Eliminar la reclamación ${reclamacion.numero_referencia}?\n\nEsta acción no se puede deshacer y eliminará todos los documentos asociados.`)) return;
    try {
      await extrajudicialAPI.delete(id);
      toast({ title: 'Eliminada', description: `Reclamación ${reclamacion.numero_referencia} eliminada correctamente` });
      navigate('/expedientes');
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudo eliminar la reclamación', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!reclamacion) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Reclamación no encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/expedientes')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver
        </Button>
      </div>
    );
  }

  const estadoInfo = ESTADOS_CONFIG[reclamacion.estado] || ESTADOS_CONFIG.borrador;
  const EstadoIcon = estadoInfo.icon;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/expedientes')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{reclamacion.numero_referencia}</h1>
              <Badge className={estadoInfo.color}>
                <EstadoIcon className="w-3 h-3 mr-1" />
                {estadoInfo.nombre}
              </Badge>
            </div>
            <p className="text-gray-600">{reclamacion.cliente_nombre}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            onClick={handleDeleteReclamacion}
            data-testid="delete-reclamacion-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Eliminar
          </Button>
          <Button variant="outline" onClick={() => setIsEstadoDialogOpen(true)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Cambiar Estado
          </Button>
          {reclamacion.estado === 'finalizado' && !reclamacion.expediente_id && (
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleConvertirExpediente}>
              <Briefcase className="w-4 h-4 mr-2" />
              Convertir a Expediente
            </Button>
          )}
        </div>
      </div>

      {/* Resumen de totales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <p className="text-sm opacity-80">Total Reclamación</p>
            <p className="text-2xl font-bold">{formatCurrency(reclamacion.total_reclamacion)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Principal</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(reclamacion.total_principal)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Intereses</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(reclamacion.total_intereses)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Conceptos</p>
            <p className="text-xl font-bold">{reclamacion.conceptos?.length || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de fases */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="datos" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            1. Datos
          </TabsTrigger>
          <TabsTrigger value="clausulas" className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            2. Cláusulas
          </TabsTrigger>
          <TabsTrigger value="documentos" className="flex items-center gap-2">
            <FileSignature className="w-4 h-4" />
            3. Documentos
          </TabsTrigger>
          <TabsTrigger value="seguimiento" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            4. Seguimiento
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: DATOS DE LA HIPOTECA */}
        <TabsContent value="datos" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Datos del cliente */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="w-5 h-5" />
                  Datos del Cliente
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => setIsEditClienteDialogOpen(true)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Editar
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Nombre</Label>
                    <p className="font-medium">{reclamacion.cliente_nombre}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">DNI/NIF</Label>
                    <p className="font-medium">{reclamacion.cliente_dni || '-'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-gray-500">Dirección</Label>
                  <p className="font-medium">{reclamacion.cliente_direccion || '-'}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Teléfono</Label>
                    <p className="font-medium">{reclamacion.cliente_telefono || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <p className="font-medium">{reclamacion.cliente_email || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Datos de la hipoteca */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="w-5 h-5" />
                  Datos de la Hipoteca
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fecha firma hipoteca</Label>
                    <Input
                      type="date"
                      value={datosHipoteca.fecha_firma_hipoteca}
                      onChange={(e) => setDatosHipoteca({...datosHipoteca, fecha_firma_hipoteca: e.target.value})}
                    />
                  </div>
                  
                  {/* Campo Entidad Bancaria - Búsqueda igual que clientes */}
                  <div className="relative">
                    <Label>Entidad bancaria actual</Label>
                    {!isCreatingBanco ? (
                      <>
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              className="pl-10"
                              value={datosHipoteca.entidad_bancaria_id ? datosHipoteca.entidad_bancaria_actual : searchBanco}
                              onChange={(e) => {
                                setSearchBanco(e.target.value);
                                setDatosHipoteca({...datosHipoteca, entidad_bancaria_actual: '', entidad_bancaria_id: null});
                                if (e.target.value.length >= 2) {
                                  handleSearchBancos(e.target.value);
                                  setShowBancoDropdown(true);
                                } else {
                                  setBancosEncontrados([]);
                                  setShowBancoDropdown(false);
                                }
                              }}
                              onFocus={() => {
                                if (searchBanco.length >= 2) setShowBancoDropdown(true);
                              }}
                              placeholder="Buscar banco por nombre o CIF..."
                            />
                          </div>
                          <Button 
                            type="button"
                            variant="outline" 
                            onClick={() => handleSearchBancos(searchBanco)}
                            disabled={loadingBancos || searchBanco.length < 2}
                          >
                            {loadingBancos ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                          </Button>
                        </div>

                        {/* Banco seleccionado */}
                        {datosHipoteca.entidad_bancaria_id && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-800">{datosHipoteca.entidad_bancaria_actual}</span>
                              <Badge className="bg-green-100 text-green-700 text-xs">Vinculado</Badge>
                            </div>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setDatosHipoteca({...datosHipoteca, entidad_bancaria_actual: '', entidad_bancaria_id: null});
                                setSearchBanco('');
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}

                        {/* Resultados de búsqueda */}
                        {showBancoDropdown && bancosEncontrados.length > 0 && (
                          <div className="mt-2 border rounded-lg divide-y max-h-48 overflow-y-auto">
                            {bancosEncontrados.map(banco => (
                              <button
                                key={banco.id}
                                type="button"
                                className="w-full p-3 text-left hover:bg-gray-50 flex items-center justify-between"
                                onClick={() => handleSelectBanco(banco)}
                              >
                                <div>
                                  <p className="font-medium">{banco.nombre || banco.razon_social}</p>
                                  <p className="text-xs text-gray-500">{banco.nif_cif} {banco.ciudad && `· ${banco.ciudad}`}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </button>
                            ))}
                          </div>
                        )}

                        {/* No hay resultados */}
                        {showBancoDropdown && searchBanco.length >= 2 && bancosEncontrados.length === 0 && !loadingBancos && (
                          <div className="mt-2 p-3 bg-gray-50 border rounded-lg text-center">
                            <p className="text-sm text-gray-500 mb-2">No se encontró "{searchBanco}"</p>
                            <Button 
                              type="button"
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setIsCreatingBanco(true);
                                setNuevoBanco({ ...nuevoBanco, nombre: searchBanco, razon_social: searchBanco });
                                setShowBancoDropdown(false);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Crear nueva entidad bancaria
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Formulario crear nueva entidad bancaria - Completo como contactos */
                      <div className="mt-2 p-4 border-2 border-blue-200 bg-blue-50 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-blue-800">Nueva Entidad Bancaria</p>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setIsCreatingBanco(false)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <Label className="text-xs">Nombre comercial *</Label>
                            <Input
                              value={nuevoBanco.nombre}
                              onChange={(e) => setNuevoBanco({...nuevoBanco, nombre: e.target.value})}
                              placeholder="Ej: BBVA, Santander, CaixaBank..."
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Razón Social</Label>
                            <Input
                              value={nuevoBanco.razon_social}
                              onChange={(e) => setNuevoBanco({...nuevoBanco, razon_social: e.target.value})}
                              placeholder="Banco Bilbao Vizcaya Argentaria S.A."
                            />
                          </div>
                          <div>
                            <Label className="text-xs">CIF</Label>
                            <Input
                              value={nuevoBanco.cif}
                              onChange={(e) => setNuevoBanco({...nuevoBanco, cif: e.target.value})}
                              placeholder="A12345678"
                            />
                          </div>
                        </div>

                        <div className="border-t pt-3">
                          <p className="text-xs font-medium text-gray-500 mb-2">DIRECCIÓN</p>
                          <div className="space-y-2">
                            <Input
                              value={nuevoBanco.direccion}
                              onChange={(e) => setNuevoBanco({...nuevoBanco, direccion: e.target.value})}
                              placeholder="Calle, número..."
                            />
                            <div className="grid grid-cols-3 gap-2">
                              <Input
                                value={nuevoBanco.codigo_postal}
                                onChange={(e) => setNuevoBanco({...nuevoBanco, codigo_postal: e.target.value})}
                                placeholder="C.P."
                              />
                              <Input
                                value={nuevoBanco.ciudad}
                                onChange={(e) => setNuevoBanco({...nuevoBanco, ciudad: e.target.value})}
                                placeholder="Ciudad"
                              />
                              <Input
                                value={nuevoBanco.provincia}
                                onChange={(e) => setNuevoBanco({...nuevoBanco, provincia: e.target.value})}
                                placeholder="Provincia"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <Label className="text-xs">Notas</Label>
                          <Textarea
                            value={nuevoBanco.notas}
                            onChange={(e) => setNuevoBanco({...nuevoBanco, notas: e.target.value})}
                            placeholder="Observaciones..."
                            rows={2}
                          />
                        </div>

                        <Button 
                          type="button"
                          className="w-full" 
                          onClick={handleCrearBanco}
                          disabled={saving || !nuevoBanco.nombre}
                        >
                          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                          Crear Entidad y Seleccionar
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label>Entidad bancaria anterior (si hubo fusión)</Label>
                  <Input
                    value={datosHipoteca.entidad_bancaria_anterior}
                    onChange={(e) => setDatosHipoteca({...datosHipoteca, entidad_bancaria_anterior: e.target.value})}
                    placeholder="Banco original antes de fusión"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={datosHipoteca.hipoteca_vigente}
                      onCheckedChange={(c) => setDatosHipoteca({...datosHipoteca, hipoteca_vigente: c})}
                    />
                    <Label>Hipoteca vigente</Label>
                  </div>
                  {!datosHipoteca.hipoteca_vigente && (
                    <div className="flex-1">
                      <Input
                        type="date"
                        value={datosHipoteca.fecha_cancelacion_hipoteca}
                        onChange={(e) => setDatosHipoteca({...datosHipoteca, fecha_cancelacion_hipoteca: e.target.value})}
                        placeholder="Fecha cancelación"
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre del Notario</Label>
                    <Input
                      value={datosHipoteca.nombre_notario}
                      onChange={(e) => setDatosHipoteca({...datosHipoteca, nombre_notario: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Número de protocolo</Label>
                    <Input
                      value={datosHipoteca.numero_protocolo}
                      onChange={(e) => setDatosHipoteca({...datosHipoteca, numero_protocolo: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label>Localidad del Notario</Label>
                  <Input
                    value={datosHipoteca.localidad_notario}
                    onChange={(e) => setDatosHipoteca({...datosHipoteca, localidad_notario: e.target.value})}
                  />
                </div>

                <Button onClick={handleSaveDatosHipoteca} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Datos Hipoteca
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 2: CLÁUSULAS Y CALCULADORA */}
        <TabsContent value="clausulas" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Selección de cláusulas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Selección de Cláusulas a Reclamar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Gastos */}
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      checked={clausulas.clausula_gastos}
                      onCheckedChange={(c) => setClausulas({...clausulas, clausula_gastos: c})}
                    />
                    <Label className="font-medium">Gastos Hipotecarios</Label>
                  </div>
                  <p className="text-sm text-gray-500 ml-6">Notaría (50%), Registro, Gestoría, Tasación (100%)</p>
                </div>

                {/* Comisión apertura */}
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      checked={clausulas.clausula_comision_apertura}
                      onCheckedChange={(c) => setClausulas({...clausulas, clausula_comision_apertura: c})}
                    />
                    <Label className="font-medium">Comisión de Apertura</Label>
                  </div>
                  <p className="text-sm text-gray-500 ml-6">Reclamable al 100% + Intereses</p>
                </div>

                {/* Posiciones deudoras */}
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      checked={clausulas.clausula_posiciones_deudoras}
                      onCheckedChange={(c) => setClausulas({...clausulas, clausula_posiciones_deudoras: c})}
                    />
                    <Label className="font-medium">Posiciones Deudoras</Label>
                  </div>
                  {clausulas.clausula_posiciones_deudoras && (
                    <div className="ml-6 mt-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={clausulas.posiciones_deudoras_con_soportes}
                          onCheckedChange={(c) => setClausulas({...clausulas, posiciones_deudoras_con_soportes: c})}
                        />
                        <Label className="text-sm">Tengo soportes (documentos)</Label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-3 text-gray-700">Cláusulas de Nulidad (sin importe)</p>
                  
                  {CLAUSULAS_NULIDAD.map((cl) => (
                    <div key={cl.id} className="flex items-center gap-2 py-2">
                      <Checkbox
                        checked={clausulas[cl.id]}
                        onCheckedChange={(c) => setClausulas({...clausulas, [cl.id]: c})}
                      />
                      <div>
                        <Label className="font-medium">{cl.nombre}</Label>
                        <p className="text-xs text-gray-500">{cl.descripcion}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Cláusula personalizada */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium mb-3 text-gray-700 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Cláusula Personalizada
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Si existe alguna cláusula abusiva que no está en la lista, puede añadirla manualmente aquí.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Nombre de la cláusula</Label>
                      <Input
                        placeholder="Ej: Cláusula de vencimiento anticipado"
                        value={clausulas.clausula_personalizada_nombre || ''}
                        onChange={(e) => setClausulas({...clausulas, clausula_personalizada_nombre: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Descripción / Motivo de reclamación</Label>
                      <Textarea
                        placeholder="Describa la cláusula y por qué considera que es abusiva..."
                        rows={3}
                        value={clausulas.clausula_personalizada || ''}
                        onChange={(e) => setClausulas({...clausulas, clausula_personalizada: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <Button onClick={handleSaveClausulas} disabled={saving} className="w-full mt-4">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Cláusulas
                </Button>
              </CardContent>
            </Card>

            {/* Conceptos y cálculos */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Conceptos Reclamados</CardTitle>
                <Button size="sm" onClick={() => setIsConceptoDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir
                </Button>
              </CardHeader>
              <CardContent>
                {reclamacion.conceptos?.length > 0 ? (
                  <div className="space-y-3">
                    {reclamacion.conceptos.map((concepto) => {
                      const tipoInfo = TIPOS_CONCEPTO.find(t => t.id === concepto.tipo) || {};
                      return (
                        <div key={concepto.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{tipoInfo.nombre || concepto.tipo}</span>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteConcepto(concepto.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="text-gray-500">Pagado:</span>
                              <p>{formatCurrency(concepto.importe_pagado)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Principal:</span>
                              <p>{formatCurrency(concepto.importe_principal)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Intereses:</span>
                              <p className="text-green-600">{formatCurrency(concepto.importe_intereses)}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t">
                            <span className="text-sm text-gray-500">
                              {concepto.dias_intereses} días · {concepto.tasa_interes}%
                            </span>
                            <span className="font-bold">{formatCurrency(concepto.importe_total)}</span>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Desglose de totales */}
                    <div className="p-4 bg-indigo-50 rounded-lg border-2 border-indigo-200 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Total Principal</span>
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(reclamacion.total_principal)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Total Intereses <span className="text-xs text-gray-400">(orientativo)</span></span>
                        <span className="font-semibold text-green-600">
                          + {formatCurrency(reclamacion.total_intereses)}
                        </span>
                      </div>
                      <div className="border-t border-indigo-200 pt-2 mt-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-indigo-900">TOTAL RECLAMACIÓN</span>
                          <span className="text-2xl font-bold text-indigo-600">
                            {formatCurrency(reclamacion.total_reclamacion)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calculator className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No hay conceptos añadidos</p>
                    <Button variant="outline" className="mt-4" onClick={() => setIsConceptoDialogOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Añadir primer concepto
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 3: DOCUMENTOS */}
        <TabsContent value="documentos" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Generación de documentos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Generar Documentos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Hoja de Encargo</p>
                      <p className="text-sm text-gray-500">Contrato de servicios con el cliente</p>
                    </div>
                    <Button onClick={() => handleGenerarDocumento('hoja_encargo')}>
                      <FileSignature className="w-4 h-4 mr-2" />
                      Generar
                    </Button>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Reclamación Extrajudicial</p>
                      <p className="text-sm text-gray-500">Documento para enviar al banco</p>
                    </div>
                    <Button onClick={() => handleGenerarDocumento('reclamacion')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Generar
                    </Button>
                  </div>
                </div>

                <div className="p-4 border rounded-lg bg-blue-50 border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-900">Resumen para el Cliente</p>
                      <p className="text-sm text-blue-700">Informe PDF con todos los datos de la reclamación</p>
                    </div>
                    <Button variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-100" onClick={handleGenerarResumenPDF}>
                      <FileCheck className="w-4 h-4 mr-2" />
                      Generar PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lista de documentos */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Documentos</CardTitle>
                <Button size="sm" onClick={() => setIsUploadDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-1" />
                  Subir
                </Button>
              </CardHeader>
              <CardContent>
                {reclamacion.documentos?.length > 0 ? (
                  <div className="space-y-2">
                    {reclamacion.documentos.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className={`w-5 h-5 ${doc.es_generado ? 'text-indigo-500' : 'text-gray-500'}`} />
                          <div>
                            <p className="font-medium text-sm">{doc.nombre_original || doc.nombre_archivo}</p>
                            <p className="text-xs text-gray-500">
                              {doc.tipo} · {formatDate(doc.created_at)}
                              {doc.es_generado && <Badge className="ml-2 bg-indigo-100 text-indigo-700">Generado</Badge>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handlePreviewDocumento(doc.id, doc.nombre_original || doc.nombre_archivo)}
                            title="Previsualizar"
                          >
                            <Eye className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadDocumento(doc.id, doc.nombre_original)} title="Descargar">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteDocumento(doc.id)} title="Eliminar">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No hay documentos</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAB 4: SEGUIMIENTO */}
        <TabsContent value="seguimiento" className="space-y-4">
          {/* Timeline de flujo de trabajo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Flujo de Tramitación Extrajudicial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Línea de conexión */}
                <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gray-200" />
                
                <div className="space-y-6">
                  {/* Paso 1: Firma del cliente */}
                  <div className="relative flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 border-2 ${
                      reclamacion.fecha_firma_cliente 
                        ? 'bg-green-100 border-green-500 text-green-700' 
                        : 'bg-gray-100 border-gray-300 text-gray-400'
                    }`}>
                      <PenTool className="w-5 h-5" />
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold flex items-center gap-2">
                            1. Firma del Cliente
                            {reclamacion.fecha_firma_cliente && <CheckCircle className="w-4 h-4 text-green-500" />}
                          </h4>
                          {reclamacion.fecha_firma_cliente ? (
                            <p className="text-sm text-gray-600 mt-1">
                              Firmado el {formatDate(reclamacion.fecha_firma_cliente)}
                              {reclamacion.documento_firma_id && (
                                <Badge className="ml-2 bg-blue-100 text-blue-700">Documento adjunto</Badge>
                              )}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 mt-1">Pendiente de firma de la hoja de encargo</p>
                          )}
                        </div>
                        {!reclamacion.fecha_firma_cliente ? (
                          <Button size="sm" onClick={() => setIsFirmaDialogOpen(true)}>
                            <FileSignature className="w-4 h-4 mr-1" />
                            Registrar Firma
                          </Button>
                        ) : !reclamacion.documento_firma_id && (
                          <Button size="sm" variant="outline" onClick={() => setIsFirmaDialogOpen(true)}>
                            <Upload className="w-4 h-4 mr-1" />
                            Adjuntar Escaneado
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Paso 2: Envío al banco */}
                  <div className="relative flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 border-2 ${
                      reclamacion.fecha_envio_banco 
                        ? 'bg-green-100 border-green-500 text-green-700' 
                        : reclamacion.fecha_firma_cliente
                          ? 'bg-yellow-100 border-yellow-500 text-yellow-700'
                          : 'bg-gray-100 border-gray-300 text-gray-400'
                    }`}>
                      <Send className="w-5 h-5" />
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold flex items-center gap-2">
                            2. Envío al Banco
                            {reclamacion.fecha_envio_banco && <CheckCircle className="w-4 h-4 text-green-500" />}
                          </h4>
                          {reclamacion.fecha_envio_banco ? (
                            <p className="text-sm text-gray-600 mt-1">
                              Enviado el {formatDate(reclamacion.fecha_envio_banco)}
                              {reclamacion.medio_envio && ` por ${reclamacion.medio_envio.replace('_', ' ')}`}
                              {reclamacion.documento_envio_id && (
                                <Badge className="ml-2 bg-blue-100 text-blue-700">Acuse adjunto</Badge>
                              )}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 mt-1">Pendiente de enviar la reclamación</p>
                          )}
                        </div>
                        {reclamacion.fecha_firma_cliente && !reclamacion.fecha_envio_banco && (
                          <Button size="sm" onClick={() => setIsEnvioDialogOpen(true)}>
                            <Mail className="w-4 h-4 mr-1" />
                            Registrar Envío
                          </Button>
                        )}
                        {reclamacion.fecha_envio_banco && (
                          <Button size="sm" variant="outline" onClick={() => setIsEnvioDialogOpen(true)}>
                            <Upload className="w-4 h-4 mr-1" />
                            Adjuntar Documentos
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Paso 3: Respuesta del banco */}
                  <div className="relative flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 border-2 ${
                      reclamacion.fecha_respuesta_banco 
                        ? 'bg-green-100 border-green-500 text-green-700' 
                        : reclamacion.fecha_envio_banco
                          ? 'bg-orange-100 border-orange-500 text-orange-700'
                          : 'bg-gray-100 border-gray-300 text-gray-400'
                    }`}>
                      <FileInput className="w-5 h-5" />
                    </div>
                    <div className="flex-1 bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold flex items-center gap-2">
                            3. Respuesta del Banco
                            {reclamacion.fecha_respuesta_banco && <CheckCircle className="w-4 h-4 text-green-500" />}
                          </h4>
                          {reclamacion.fecha_respuesta_banco ? (
                            <div className="mt-1">
                              <p className="text-sm text-gray-600">
                                Recibida el {formatDate(reclamacion.fecha_respuesta_banco)}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={RESULTADOS_CONFIG[reclamacion.resultado]?.color || 'bg-gray-100'}>
                                  {RESULTADOS_CONFIG[reclamacion.resultado]?.nombre || reclamacion.resultado}
                                </Badge>
                                {reclamacion.importe_ofrecido > 0 && (
                                  <span className="text-sm font-medium text-green-600">
                                    {formatCurrency(reclamacion.importe_ofrecido)} ofrecido
                                  </span>
                                )}
                              </div>
                              {reclamacion.observaciones_respuesta && (
                                <p className="text-sm text-gray-600 mt-2 italic">
                                  &ldquo;{reclamacion.observaciones_respuesta}&rdquo;
                                </p>
                              )}
                            </div>
                          ) : reclamacion.fecha_envio_banco ? (
                            <p className="text-sm text-orange-600 mt-1 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Esperando respuesta del banco...
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 mt-1">Pendiente del paso anterior</p>
                          )}
                        </div>
                        {reclamacion.fecha_envio_banco && !reclamacion.fecha_respuesta_banco && (
                          <Button size="sm" onClick={() => setIsRespuestaDialogOpen(true)}>
                            <FileInput className="w-4 h-4 mr-1" />
                            Registrar Respuesta
                          </Button>
                        )}
                        {reclamacion.fecha_respuesta_banco && (
                          <Button size="sm" variant="outline" onClick={() => setIsRespuestaDialogOpen(true)}>
                            <Upload className="w-4 h-4 mr-1" />
                            Adjuntar Documentos
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Paso 4: Crear expediente judicial */}
                  <div className="relative flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center z-10 border-2 ${
                      reclamacion.expediente_id 
                        ? 'bg-purple-100 border-purple-500 text-purple-700' 
                        : reclamacion.estado === 'finalizado'
                          ? 'bg-purple-100 border-purple-500 text-purple-700 animate-pulse'
                          : 'bg-gray-100 border-gray-300 text-gray-400'
                    }`}>
                      <Gavel className="w-5 h-5" />
                    </div>
                    <div className={`flex-1 rounded-lg p-4 ${
                      reclamacion.estado === 'finalizado' && !reclamacion.expediente_id 
                        ? 'bg-purple-50 border-2 border-purple-200' 
                        : 'bg-gray-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold flex items-center gap-2">
                            4. Crear Expediente Judicial
                            {reclamacion.expediente_id && <CheckCircle className="w-4 h-4 text-purple-500" />}
                          </h4>
                          {reclamacion.expediente_id ? (
                            <p className="text-sm text-purple-600 mt-1">
                              Expediente creado. <Button variant="link" className="p-0 h-auto text-purple-700" onClick={() => navigate(`/expedientes/${reclamacion.expediente_id}`)}>
                                Ver expediente →
                              </Button>
                            </p>
                          ) : reclamacion.estado === 'finalizado' ? (
                            <p className="text-sm text-purple-700 mt-1 font-medium">
                              ¡Reclamación finalizada! Ya puede proceder a crear el expediente judicial.
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500 mt-1">Disponible al finalizar la reclamación</p>
                          )}
                        </div>
                        {reclamacion.estado === 'finalizado' && !reclamacion.expediente_id && (
                          <Button 
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={() => setIsConvertirDialogOpen(true)}
                          >
                            <Gavel className="w-4 h-4 mr-2" />
                            Proceder a Crear Expediente
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Historial completo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Historial de Actuaciones</CardTitle>
            </CardHeader>
            <CardContent>
              {reclamacion.historial?.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {reclamacion.historial.map((h) => {
                    let IconComponent = Clock;
                    let iconColor = 'text-gray-500';
                    if (h.accion === 'FIRMA_CLIENTE') { IconComponent = PenTool; iconColor = 'text-green-500'; }
                    else if (h.accion === 'ENVIO_BANCO') { IconComponent = Send; iconColor = 'text-blue-500'; }
                    else if (h.accion === 'RESPUESTA_BANCO') { IconComponent = FileInput; iconColor = 'text-orange-500'; }
                    else if (h.accion === 'DOCUMENTO_ADJUNTADO') { IconComponent = FileText; iconColor = 'text-indigo-500'; }
                    else if (h.accion === 'CONVERSION_EXPEDIENTE') { IconComponent = Gavel; iconColor = 'text-purple-500'; }
                    else if (h.accion === 'GENERACION_DOCUMENTO') { IconComponent = FileCheck; iconColor = 'text-cyan-500'; }
                    
                    return (
                      <div key={h.id} className="flex gap-3 pb-3 border-b last:border-0">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <IconComponent className={`w-4 h-4 ${iconColor}`} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{h.accion.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-gray-600">{h.descripcion}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {h.usuario_nombre} · {formatDate(h.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">Sin historial</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ConceptoDialog
        isOpen={isConceptoDialogOpen}
        onClose={() => setIsConceptoDialogOpen(false)}
        reclamacionId={id}
        clausulas={clausulas}
        onSuccess={loadReclamacion}
      />

      <CambiarEstadoDialog
        isOpen={isEstadoDialogOpen}
        onClose={() => setIsEstadoDialogOpen(false)}
        reclamacionId={id}
        estadoActual={reclamacion.estado}
        onSuccess={loadReclamacion}
      />

      <UploadDocumentoDialog
        isOpen={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        reclamacionId={id}
        onSuccess={loadReclamacion}
      />

      {/* Dialog de Previsualización de Documento */}
      <Dialog open={previewDialog.open} onOpenChange={(open) => setPreviewDialog({ ...previewDialog, open })}>
        <DialogContent className="max-w-5xl h-[85vh] p-0">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {previewDialog.nombre}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open(previewDialog.url, '_blank')}
              >
                <ArrowRight className="w-4 h-4 mr-1" />
                Abrir en nueva pestaña
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden h-full">
            <iframe
              src={previewDialog.url}
              className="w-full h-[calc(85vh-80px)] border-0"
              title="Previsualización del documento"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Firma Cliente */}
      <FirmaClienteDialog
        isOpen={isFirmaDialogOpen}
        onClose={() => setIsFirmaDialogOpen(false)}
        reclamacionId={id}
        reclamacion={reclamacion}
        onSuccess={loadReclamacion}
      />

      {/* Dialog Envío al Banco */}
      <EnvioBancoDialog
        isOpen={isEnvioDialogOpen}
        onClose={() => setIsEnvioDialogOpen(false)}
        reclamacionId={id}
        reclamacion={reclamacion}
        onSuccess={loadReclamacion}
      />

      {/* Dialog Respuesta del Banco */}
      <RespuestaBancoDialog
        isOpen={isRespuestaDialogOpen}
        onClose={() => setIsRespuestaDialogOpen(false)}
        reclamacionId={id}
        reclamacion={reclamacion}
        onSuccess={loadReclamacion}
      />

      {/* Dialog Convertir a Expediente */}
      <ConvertirExpedienteDialog
        isOpen={isConvertirDialogOpen}
        onClose={() => setIsConvertirDialogOpen(false)}
        reclamacionId={id}
        reclamacion={reclamacion}
        onSuccess={(expedienteId) => {
          loadReclamacion();
          toast({ 
            title: '¡Expediente creado!', 
            description: 'La reclamación se ha convertido en expediente judicial' 
          });
        }}
        navigate={navigate}
      />

      {/* Dialog Editar Datos del Cliente */}
      <EditarClienteDialog
        isOpen={isEditClienteDialogOpen}
        onClose={() => setIsEditClienteDialogOpen(false)}
        reclamacionId={id}
        reclamacion={reclamacion}
        onSuccess={loadReclamacion}
      />
    </div>
  );

  async function handleDeleteConcepto(conceptoId) {
    if (!window.confirm('¿Eliminar este concepto?')) return;
    try {
      await extrajudicialAPI.deleteConcepto(id, conceptoId);
      toast({ title: 'Concepto eliminado' });
      loadReclamacion();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  }
};

// =============================================================================
// DIALOG: Añadir Concepto
// =============================================================================
const ConceptoDialog = ({ isOpen, onClose, reclamacionId, clausulas, onSuccess }) => {
  const [tipo, setTipo] = useState('');
  const [importePagado, setImportePagado] = useState('');
  const [fechaPago, setFechaPago] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [calcularIntereses, setCalcularIntereses] = useState(false);
  const [calculo, setCalculo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTipo('');
      setImportePagado('');
      setFechaPago('');
      setDescripcion('');
      setCalcularIntereses(false);
      setCalculo(null);
    }
  }, [isOpen]);

  const handleCalcular = async () => {
    if (!tipo || !importePagado) return;
    try {
      const res = await extrajudicialAPI.calcularConcepto(tipo, parseFloat(importePagado), fechaPago || null);
      setCalculo(res.data);
      setCalcularIntereses(true); // Activar automáticamente si el usuario pulsa calcular
    } catch (error) {
      toast({ title: 'Error calculando', variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!tipo) {
      toast({ title: 'Error', description: 'Selecciona un tipo', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      await extrajudicialAPI.addConcepto(reclamacionId, {
        tipo,
        descripcion,
        importe_pagado: parseFloat(importePagado) || 0,
        fecha_pago: fechaPago || null,
        calcular_intereses: calcularIntereses
      });
      toast({ title: 'Concepto añadido' });
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Filtrar tipos disponibles según cláusulas seleccionadas
  const tiposDisponibles = TIPOS_CONCEPTO.filter(t => {
    if (t.grupo === 'gastos' && clausulas.clausula_gastos) return true;
    if (t.grupo === 'apertura' && clausulas.clausula_comision_apertura) return true;
    if (t.grupo === 'posiciones' && clausulas.clausula_posiciones_deudoras) return true;
    return false;
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Añadir Concepto Reclamado</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Tipo de concepto</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo..." />
              </SelectTrigger>
              <SelectContent>
                {tiposDisponibles.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.nombre} ({t.porcentaje}%)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Importe pagado (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={importePagado}
                onChange={(e) => setImportePagado(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label>Fecha de pago</Label>
              <Input
                type="date"
                value={fechaPago}
                onChange={(e) => setFechaPago(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label>Descripción (opcional)</Label>
            <Input
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej: Factura notaría..."
            />
          </div>

          {/* Checkbox para calcular intereses */}
          <div className="flex items-center space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <Checkbox
              id="calcularIntereses"
              checked={calcularIntereses}
              onCheckedChange={setCalcularIntereses}
            />
            <label htmlFor="calcularIntereses" className="text-sm font-medium text-amber-900 cursor-pointer">
              Calcular intereses legales
            </label>
            <span className="text-xs text-amber-600 ml-2">(orientativo)</span>
          </div>

          {calcularIntereses && (
            <Button variant="outline" className="w-full" onClick={handleCalcular} disabled={!tipo || !importePagado}>
              <Calculator className="w-4 h-4 mr-2" />
              Calcular Intereses
            </Button>
          )}

          {calculo && calcularIntereses && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-2">Resultado del Cálculo</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Principal:</span>
                  <p className="font-bold">{formatCurrency(calculo.importe_principal)}</p>
                </div>
                <div>
                  <span className="text-gray-600">Intereses:</span>
                  <p className="font-bold text-green-600">{formatCurrency(calculo.importe_intereses)}</p>
                </div>
                <div>
                  <span className="text-gray-600">Días:</span>
                  <p>{calculo.dias_intereses}</p>
                </div>
                <div>
                  <span className="text-gray-600">Tasa:</span>
                  <p>{calculo.tasa_interes}%</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-green-200">
                <span className="text-sm text-gray-600">Total:</span>
                <p className="text-xl font-bold text-green-700">{formatCurrency(calculo.total)}</p>
              </div>
            </div>
          )}

          {!calcularIntereses && importePagado && tipo && (
            <div className="p-3 bg-gray-50 rounded-lg border text-sm">
              <p className="text-gray-600">
                <strong>Sin intereses:</strong> Se añadirá solo el importe principal reclamable.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !tipo}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Añadir Concepto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Cambiar Estado
// =============================================================================
const CambiarEstadoDialog = ({ isOpen, onClose, reclamacionId, estadoActual, onSuccess }) => {
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [fechaEnvio, setFechaEnvio] = useState('');
  const [fechaRespuesta, setFechaRespuesta] = useState('');
  const [resultado, setResultado] = useState('');
  const [importeOfrecido, setImporteOfrecido] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNuevoEstado('');
      setFechaEnvio('');
      setFechaRespuesta('');
      setResultado('');
      setImporteOfrecido('');
      setObservaciones('');
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!nuevoEstado) {
      toast({ title: 'Error', description: 'Selecciona un estado', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await extrajudicialAPI.cambiarEstado(reclamacionId, {
        nuevo_estado: nuevoEstado,
        fecha_envio: fechaEnvio || null,
        fecha_respuesta: fechaRespuesta || null,
        resultado: resultado || null,
        importe_ofrecido: importeOfrecido ? parseFloat(importeOfrecido) : null,
        observaciones: observaciones || null
      });
      toast({ title: 'Estado actualizado' });
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Cambiar Estado</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Nuevo Estado</Label>
            <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar estado..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ESTADOS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key} disabled={key === estadoActual}>
                    {config.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(nuevoEstado === 'enviado_banco' || nuevoEstado === 'pendiente_respuesta') && (
            <div>
              <Label>Fecha de envío al banco</Label>
              <Input
                type="date"
                value={fechaEnvio}
                onChange={(e) => setFechaEnvio(e.target.value)}
              />
            </div>
          )}

          {nuevoEstado === 'finalizado' && (
            <>
              <div>
                <Label>Fecha de respuesta</Label>
                <Input
                  type="date"
                  value={fechaRespuesta}
                  onChange={(e) => setFechaRespuesta(e.target.value)}
                />
              </div>
              <div>
                <Label>Resultado</Label>
                <Select value={resultado} onValueChange={setResultado}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar resultado..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estimada">Estimada</SelectItem>
                    <SelectItem value="desestimada">Desestimada</SelectItem>
                    <SelectItem value="parcial">Parcialmente Estimada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(resultado === 'estimada' || resultado === 'parcial') && (
                <div>
                  <Label>Importe ofrecido (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={importeOfrecido}
                    onChange={(e) => setImporteOfrecido(e.target.value)}
                  />
                </div>
              )}
            </>
          )}

          <div>
            <Label>Observaciones</Label>
            <Textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Notas sobre este cambio..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Subir Documento
// =============================================================================
const UploadDocumentoDialog = ({ isOpen, onClose, reclamacionId, onSuccess }) => {
  const [tipo, setTipo] = useState('escritura');
  const [descripcion, setDescripcion] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const TIPOS_DOCUMENTO = [
    { id: 'escritura', nombre: 'Escritura Hipotecaria' },
    { id: 'hoja_encargo_firmada', nombre: 'Hoja de Encargo Firmada' },
    { id: 'reclamacion_enviada', nombre: 'Reclamación Enviada' },
    { id: 'respuesta_banco', nombre: 'Respuesta del Banco' },
    { id: 'otro', nombre: 'Otro Documento' },
  ];

  useEffect(() => {
    if (isOpen) {
      setTipo('escritura');
      setDescripcion('');
      setFile(null);
    }
  }, [isOpen]);

  const handleUpload = async () => {
    if (!file) {
      toast({ title: 'Error', description: 'Selecciona un archivo', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', tipo);
      formData.append('descripcion', descripcion);

      await extrajudicialAPI.uploadDocumento(reclamacionId, formData);
      toast({ title: 'Documento subido' });
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo subir el archivo', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Subir Documento</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Tipo de documento</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_DOCUMENTO.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Archivo</Label>
            <div className="mt-1 border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  {file ? file.name : 'Haz clic o arrastra un archivo'}
                </p>
              </label>
            </div>
          </div>

          <div>
            <Label>Descripción (opcional)</Label>
            <Input
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripción del documento..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleUpload} disabled={saving || !file}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Subir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Firma del Cliente
// =============================================================================
const FirmaClienteDialog = ({ isOpen, onClose, reclamacionId, reclamacion, onSuccess }) => {
  const [fechaFirma, setFechaFirma] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFechaFirma(reclamacion?.fecha_firma_cliente || new Date().toISOString().split('T')[0]);
      setArchivo(null);
    }
  }, [isOpen, reclamacion]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Si no hay fecha registrada, registrar la firma
      if (!reclamacion?.fecha_firma_cliente && fechaFirma) {
        await extrajudicialAPI.registrarFirma(reclamacionId, { fecha_firma: fechaFirma });
      }
      
      // Si hay archivo, adjuntarlo
      if (archivo) {
        await extrajudicialAPI.adjuntarDocumentoSeguimiento(reclamacionId, 'firma_cliente', archivo);
      }
      
      toast({ title: 'Firma registrada correctamente' });
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Error al registrar firma', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="w-5 h-5" />
            Firma del Cliente
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Fecha de Firma</Label>
            <Input
              type="date"
              value={fechaFirma}
              onChange={(e) => setFechaFirma(e.target.value)}
              disabled={!!reclamacion?.fecha_firma_cliente}
            />
            {reclamacion?.fecha_firma_cliente && (
              <p className="text-xs text-green-600 mt-1">✓ Firma ya registrada</p>
            )}
          </div>

          <div>
            <Label>Hoja de Encargo Firmada (escaneada)</Label>
            <div className="mt-1 border-2 border-dashed rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                className="hidden"
              />
              <button onClick={() => fileInputRef.current?.click()} className="cursor-pointer w-full">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  {archivo ? archivo.name : 'Adjuntar documento escaneado'}
                </p>
              </button>
            </div>
            {reclamacion?.documento_firma_id && (
              <p className="text-xs text-green-600 mt-1">✓ Documento ya adjuntado</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || (!fechaFirma && !archivo)}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Envío al Banco (con múltiples archivos)
// =============================================================================
const EnvioBancoDialog = ({ isOpen, onClose, reclamacionId, reclamacion, onSuccess }) => {
  const [fechaEnvio, setFechaEnvio] = useState('');
  const [medioEnvio, setMedioEnvio] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFechaEnvio(reclamacion?.fecha_envio_banco || new Date().toISOString().split('T')[0]);
      setMedioEnvio(reclamacion?.medio_envio || '');
      setArchivos([]);
      setUploadProgress(0);
    }
  }, [isOpen, reclamacion]);

  const handleAddFiles = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setArchivos(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setArchivos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Si no hay fecha registrada, registrar el envío
      if (!reclamacion?.fecha_envio_banco && fechaEnvio) {
        await extrajudicialAPI.registrarEnvio(reclamacionId, { 
          fecha_envio: fechaEnvio,
          medio_envio: medioEnvio 
        });
      }
      
      // Subir todos los archivos
      if (archivos.length > 0) {
        for (let i = 0; i < archivos.length; i++) {
          setUploadProgress(Math.round(((i + 1) / archivos.length) * 100));
          await extrajudicialAPI.adjuntarDocumentoSeguimiento(reclamacionId, 'acuse_envio', archivos[i]);
        }
      }
      
      toast({ title: 'Envío registrado', description: archivos.length > 0 ? `${archivos.length} documento(s) adjuntado(s)` : '' });
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Error al registrar envío', variant: 'destructive' });
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  // Obtener documentos de envío ya adjuntos
  const documentosEnvio = reclamacion?.documentos?.filter(d => d.tipo === 'acuse_envio') || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Envío al Banco
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha de Envío</Label>
              <Input
                type="date"
                value={fechaEnvio}
                onChange={(e) => setFechaEnvio(e.target.value)}
                disabled={!!reclamacion?.fecha_envio_banco}
              />
              {reclamacion?.fecha_envio_banco && (
                <p className="text-xs text-green-600 mt-1">✓ Envío registrado</p>
              )}
            </div>

            <div>
              <Label>Medio de Envío</Label>
              <Select 
                value={medioEnvio} 
                onValueChange={setMedioEnvio}
                disabled={!!reclamacion?.fecha_envio_banco}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="burofax">Burofax</SelectItem>
                  <SelectItem value="email">Correo Electrónico</SelectItem>
                  <SelectItem value="correo_certificado">Correo Certificado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Documentos ya adjuntos */}
          {documentosEnvio.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm font-medium text-green-800 mb-2">Documentos adjuntos ({documentosEnvio.length})</p>
              <ul className="space-y-1">
                {documentosEnvio.map((doc) => (
                  <li key={doc.id} className="text-xs text-green-700 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {doc.nombre_original || doc.nombre_archivo}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Subir nuevos archivos */}
          <div>
            <Label>Adjuntar Documentos (burofax, acuse, etc.)</Label>
            <div className="mt-1 border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={handleAddFiles}
                className="hidden"
              />
              <button onClick={() => fileInputRef.current?.click()} className="cursor-pointer w-full">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Haz clic para adjuntar archivos (PDF, JPG, PNG)
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Puedes seleccionar múltiples archivos
                </p>
              </button>
            </div>
            
            {/* Lista de archivos a subir */}
            {archivos.length > 0 && (
              <div className="mt-2 space-y-1">
                {archivos.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-blue-50 rounded px-2 py-1">
                    <span className="text-xs text-blue-700 truncate flex-1">{file.name}</span>
                    <button 
                      onClick={() => handleRemoveFile(index)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Progress bar */}
          {saving && uploadProgress > 0 && (
            <div className="space-y-1">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-center text-gray-500">Subiendo... {uploadProgress}%</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || (!reclamacion?.fecha_envio_banco && !fechaEnvio)}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {archivos.length > 0 ? `Guardar y Subir (${archivos.length})` : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Respuesta del Banco (con múltiples archivos)
// =============================================================================
const RespuestaBancoDialog = ({ isOpen, onClose, reclamacionId, reclamacion, onSuccess }) => {
  const [fechaRespuesta, setFechaRespuesta] = useState('');
  const [resultado, setResultado] = useState('');
  const [importeOfrecido, setImporteOfrecido] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFechaRespuesta(reclamacion?.fecha_respuesta_banco || new Date().toISOString().split('T')[0]);
      setResultado(reclamacion?.resultado || '');
      setImporteOfrecido(reclamacion?.importe_ofrecido || '');
      setObservaciones(reclamacion?.observaciones_respuesta || '');
      setArchivos([]);
      setUploadProgress(0);
    }
  }, [isOpen, reclamacion]);

  const handleAddFiles = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setArchivos(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index) => {
    setArchivos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Si no hay respuesta registrada, registrarla
      if (!reclamacion?.fecha_respuesta_banco && fechaRespuesta && resultado) {
        await extrajudicialAPI.registrarRespuesta(reclamacionId, { 
          fecha_respuesta: fechaRespuesta,
          resultado,
          importe_ofrecido: importeOfrecido ? parseFloat(importeOfrecido) : null,
          observaciones
        });
      }
      
      // Subir todos los archivos
      if (archivos.length > 0) {
        for (let i = 0; i < archivos.length; i++) {
          setUploadProgress(Math.round(((i + 1) / archivos.length) * 100));
          await extrajudicialAPI.adjuntarDocumentoSeguimiento(reclamacionId, 'respuesta_banco', archivos[i]);
        }
      }
      
      toast({ title: 'Respuesta registrada', description: archivos.length > 0 ? `${archivos.length} documento(s) adjuntado(s)` : '' });
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Error al registrar respuesta', variant: 'destructive' });
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  // Obtener documentos de respuesta ya adjuntos
  const documentosRespuesta = reclamacion?.documentos?.filter(d => d.tipo === 'respuesta_banco') || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileInput className="w-5 h-5 text-orange-600" />
            Respuesta del Banco
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha de Respuesta</Label>
              <Input
                type="date"
                value={fechaRespuesta}
                onChange={(e) => setFechaRespuesta(e.target.value)}
                disabled={!!reclamacion?.fecha_respuesta_banco}
              />
            </div>

            <div>
              <Label>Resultado</Label>
              <Select 
                value={resultado} 
                onValueChange={setResultado}
                disabled={!!reclamacion?.fecha_respuesta_banco}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="estimada">Estimada (Aceptada)</SelectItem>
                  <SelectItem value="parcial">Parcialmente Estimada</SelectItem>
                  <SelectItem value="desestimada">Desestimada (Rechazada)</SelectItem>
                  <SelectItem value="sin_respuesta">Sin Respuesta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {(resultado === 'estimada' || resultado === 'parcial') && (
            <div>
              <Label>Importe Ofrecido por el Banco (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={importeOfrecido}
                onChange={(e) => setImporteOfrecido(e.target.value)}
                placeholder="0.00"
                disabled={!!reclamacion?.fecha_respuesta_banco}
              />
            </div>
          )}

          <div>
            <Label>Observaciones</Label>
            <Textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Resumen de la respuesta del banco..."
              rows={2}
              disabled={!!reclamacion?.fecha_respuesta_banco}
            />
          </div>

          {/* Documentos ya adjuntos */}
          {documentosRespuesta.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm font-medium text-orange-800 mb-2">Documentos adjuntos ({documentosRespuesta.length})</p>
              <ul className="space-y-1">
                {documentosRespuesta.map((doc) => (
                  <li key={doc.id} className="text-xs text-orange-700 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {doc.nombre_original || doc.nombre_archivo}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Subir nuevos archivos */}
          <div>
            <Label>Adjuntar Documentos (cartas de respuesta, ofertas, etc.)</Label>
            <div className="mt-1 border-2 border-dashed rounded-lg p-4 text-center hover:border-orange-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={handleAddFiles}
                className="hidden"
              />
              <button onClick={() => fileInputRef.current?.click()} className="cursor-pointer w-full">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Haz clic para adjuntar archivos
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Puedes seleccionar múltiples archivos (PDF, JPG, PNG)
                </p>
              </button>
            </div>
            
            {/* Lista de archivos a subir */}
            {archivos.length > 0 && (
              <div className="mt-2 space-y-1">
                {archivos.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-orange-50 rounded px-2 py-1">
                    <span className="text-xs text-orange-700 truncate flex-1">{file.name}</span>
                    <button 
                      onClick={() => handleRemoveFile(index)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Progress bar */}
          {saving && uploadProgress > 0 && (
            <div className="space-y-1">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-orange-500 transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-center text-gray-500">Subiendo... {uploadProgress}%</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || (!reclamacion?.fecha_respuesta_banco && (!fechaRespuesta || !resultado)) && archivos.length === 0}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            {archivos.length > 0 ? `Guardar y Subir (${archivos.length})` : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Convertir a Expediente
// =============================================================================
const ConvertirExpedienteDialog = ({ isOpen, onClose, reclamacionId, reclamacion, onSuccess, navigate }) => {
  const [creando, setCreando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [pasoActual, setPasoActual] = useState('');
  const [expedienteCreado, setExpedienteCreado] = useState(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay to allow animation to complete
      const timer = setTimeout(() => {
        setCreando(false);
        setProgreso(0);
        setPasoActual('');
        setExpedienteCreado(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleConvertir = async () => {
    try {
      setCreando(true);
      setProgreso(0);
      
      // Paso 1: Inicializando
      setPasoActual('Preparando datos de la reclamación...');
      setProgreso(10);
      await new Promise(r => setTimeout(r, 600));
      
      // Paso 2: Verificando cliente
      setPasoActual('Verificando información del cliente...');
      setProgreso(25);
      await new Promise(r => setTimeout(r, 500));
      
      // Paso 3: Creando expediente
      setPasoActual('Creando expediente judicial...');
      setProgreso(40);
      await new Promise(r => setTimeout(r, 400));
      
      // Paso 4: Llamada al backend
      setPasoActual('Guardando en base de datos...');
      setProgreso(55);
      
      const res = await extrajudicialAPI.convertirAExpediente(reclamacionId);
      
      if (!res.data.success) {
        throw new Error(res.data.detail || 'Error al crear el expediente');
      }
      
      // Paso 5: Creando actuaciones
      setPasoActual(`Creando ${res.data.total_actuaciones} actuaciones...`);
      setProgreso(75);
      await new Promise(r => setTimeout(r, 600));
      
      // Paso 6: Vinculando documentos
      setPasoActual('Vinculando documentos y participantes...');
      setProgreso(90);
      await new Promise(r => setTimeout(r, 400));
      
      // Completado
      setProgreso(100);
      setPasoActual('¡Expediente creado correctamente!');
      await new Promise(r => setTimeout(r, 500));
      
      // Mostrar resultado
      setExpedienteCreado(res.data);
      setCreando(false);
      
    } catch (error) {
      console.error('Error creando expediente:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || error.message || 'Error al crear el expediente', 
        variant: 'destructive' 
      });
      setProgreso(0);
      setPasoActual('');
      setCreando(false);
      setExpedienteCreado(null);
    }
  };

  const handleClose = () => {
    if (creando) return; // No cerrar si está creando
    if (expedienteCreado) {
      onSuccess(expedienteCreado.expediente_id);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open && creando) return; // No cerrar durante creación
      if (!open && expedienteCreado) {
        onSuccess(expedienteCreado.expediente_id);
      }
      onClose();
    }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {expedienteCreado ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <Gavel className="w-5 h-5 text-purple-600" />
            )}
            {expedienteCreado ? 'Expediente Creado' : 'Crear Expediente Judicial'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Estado inicial - antes de crear */}
          {!creando && !expedienteCreado && (
            <>
              <p className="text-gray-600">Se creará un nuevo expediente con todos los datos y documentos de esta reclamación extrajudicial.</p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Tipo:</span>
                  <span className="font-medium">Civil - Cláusulas Abusivas</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Cliente:</span>
                  <span className="font-medium">{reclamacion?.cliente_nombre || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Entidad Bancaria:</span>
                  <span className="font-medium">{reclamacion?.entidad_bancaria_actual || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Importe reclamado:</span>
                  <span className="font-bold text-indigo-600">{formatCurrency(reclamacion?.total_reclamacion || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Resultado extrajudicial:</span>
                  <span className="font-medium">{RESULTADOS_CONFIG[reclamacion?.resultado]?.nombre || reclamacion?.resultado}</span>
                </div>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-purple-800 mb-2">Se crearán automáticamente:</p>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-purple-500" /> Expediente con datos del caso</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-purple-500" /> Participantes (cliente y banco)</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-purple-500" /> Actuaciones del historial extrajudicial</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-purple-500" /> Vinculación de documentos</li>
                </ul>
              </div>
            </>
          )}
          
          {/* Estado: creando expediente */}
          {creando && !expedienteCreado && (
            <div className="space-y-4 py-4">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
                <p className="font-medium text-gray-900">Creando expediente...</p>
                <p className="text-sm text-gray-500 mt-1">{pasoActual}</p>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-600 transition-all duration-500 ease-out"
                    style={{ width: `${progreso}%` }}
                  />
                </div>
                <p className="text-center text-sm text-gray-500">{progreso}%</p>
              </div>
            </div>
          )}
          
          {/* Estado: expediente creado */}
          {expedienteCreado && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <span className="font-semibold text-green-800">¡Expediente creado correctamente!</span>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1 border-b border-green-100">
                    <span className="text-gray-600">Número de expediente:</span>
                    <span className="font-bold text-green-700">{expedienteCreado.numero_expediente}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-green-100">
                    <span className="text-gray-600">Cliente:</span>
                    <span className="font-medium">{expedienteCreado.cliente_nombre}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-green-100">
                    <span className="text-gray-600">Entidad Bancaria:</span>
                    <span className="font-medium">{expedienteCreado.entidad_bancaria}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-green-100">
                    <span className="text-gray-600">Importe reclamado:</span>
                    <span className="font-medium text-indigo-600">{formatCurrency(expedienteCreado.total_reclamacion)}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-green-100">
                    <span className="text-gray-600">Actuaciones creadas:</span>
                    <span className="font-bold text-blue-600">{expedienteCreado.total_actuaciones}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-gray-600">Documentos adjuntados:</span>
                    <span className="font-bold text-purple-600">{expedienteCreado.total_adjuntos || 0}</span>
                  </div>
                </div>
              </div>
              
              {/* Participantes creados */}
              {expedienteCreado.participantes_creados && expedienteCreado.participantes_creados.length > 0 && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-blue-700 mb-2">Participantes vinculados:</p>
                  <ul className="text-xs text-blue-600 space-y-1">
                    {expedienteCreado.participantes_creados.map((part, idx) => (
                      <li key={idx} className="flex items-center gap-1">
                        <User className="w-3 h-3 text-blue-500" />
                        {part}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Actuaciones creadas */}
              {expedienteCreado.actuaciones_creadas && expedienteCreado.actuaciones_creadas.length > 0 && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Actuaciones generadas:</p>
                  <ul className="text-xs text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                    {expedienteCreado.actuaciones_creadas.map((act, idx) => (
                      <li key={idx} className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        {act}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {/* Botones antes de crear */}
          {!creando && !expedienteCreado && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button 
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleConvertir}
              >
                <Gavel className="w-4 h-4 mr-2" />
                Crear Expediente
              </Button>
            </>
          )}
          
          {/* Botones después de crear */}
          {expedienteCreado && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cerrar
              </Button>
              <Button 
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => navigate(`/expedientes/${expedienteCreado.expediente_id}`)}
              >
                <Eye className="w-4 h-4 mr-2" />
                Ver Expediente
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Editar Datos del Cliente
// =============================================================================
const EditarClienteDialog = ({ isOpen, onClose, reclamacionId, reclamacion, onSuccess }) => {
  const [datosCliente, setDatosCliente] = useState({
    cliente_nombre: '',
    cliente_dni: '',
    cliente_direccion: '',
    cliente_telefono: '',
    cliente_email: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && reclamacion) {
      setDatosCliente({
        cliente_nombre: reclamacion.cliente_nombre || '',
        cliente_dni: reclamacion.cliente_dni || '',
        cliente_direccion: reclamacion.cliente_direccion || '',
        cliente_telefono: reclamacion.cliente_telefono || '',
        cliente_email: reclamacion.cliente_email || ''
      });
    }
  }, [isOpen, reclamacion]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await extrajudicialAPI.updateDatosCliente(reclamacionId, datosCliente);
      toast({ title: 'Datos del cliente actualizados' });
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudieron guardar los datos', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Editar Datos del Cliente
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Nombre completo</Label>
            <Input
              value={datosCliente.cliente_nombre}
              onChange={(e) => setDatosCliente({...datosCliente, cliente_nombre: e.target.value})}
              placeholder="Nombre del cliente"
            />
          </div>

          <div>
            <Label>DNI/NIF</Label>
            <Input
              value={datosCliente.cliente_dni}
              onChange={(e) => setDatosCliente({...datosCliente, cliente_dni: e.target.value})}
              placeholder="12345678A"
            />
          </div>

          <div>
            <Label>Dirección</Label>
            <Input
              value={datosCliente.cliente_direccion}
              onChange={(e) => setDatosCliente({...datosCliente, cliente_direccion: e.target.value})}
              placeholder="Calle, número, código postal, ciudad"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Teléfono</Label>
              <Input
                value={datosCliente.cliente_telefono}
                onChange={(e) => setDatosCliente({...datosCliente, cliente_telefono: e.target.value})}
                placeholder="600 123 456"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={datosCliente.cliente_email}
                onChange={(e) => setDatosCliente({...datosCliente, cliente_email: e.target.value})}
                placeholder="cliente@email.com"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <Save className="w-4 h-4 mr-2" />
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReclamacionDetalle;
